import { firestore } from "../../firebase.config"
import { storedVisitOrigin } from "../../utility/visitOrigin"


// The values are what office sees on the order, so existing ones keep their
// value when their label changes. An extra input asks for a detail, and it has
// to be filled in (2026-10-10). "I don't remember" is gone: old answers with that
// value are still shown as they were saved.
// "ChatGPT or another AI assistant" asks which one from a list (extraChoices,
// saved as the text, 2026-10-22): before, office couldn't tell ChatGPT from
// Gemini or Copilot. Older answers with that value have no text.
// Fewer choices (2026-10-24, Alejo): "Google Maps" alone; no "An ad" or "An
// article or blog" (people who come from our Google ads answer "Google
// search"), nor "Bing or another search engine" (hardly anyone).
const HowDidYouHearAboutUs = [
  {label: "Google search", value: "google"},
  {label: "Google Maps", value: "google maps"},
  {label: "ChatGPT or another AI assistant", value: "ai assistant", extraInput: true, extraInputLabel: "Which one?", inputRequired: true,
    extraChoices: ["ChatGPT", "Google Gemini or AI Overview", "Microsoft Copilot", "Perplexity", "Claude", "Another one"]},
  {label: "Social media", value: "social media"},
  {label: "Referred by a friend or colleague", value: "referred by a friend/colleague", extraInput: true, extraInputLabel: "Who referred you?", inputRequired: true},
  {label: "Recommended by my attorney / law office", value: "recommended by my attorney/law office", extraInput: true, extraInputLabel: "Attorney or law office", inputRequired: true},
  {label: "Other", value: "other", extraInput: true, extraInputLabel: "Tell us where", inputRequired: true},
]

// No longer offered, but saved on older answers: they still count as answered
// (nobody is asked again) and show with their name in office.
const RETIRED_HOW_DID_YOU_HEAR = [
  {label: "Bing or another search engine", value: "bing"},
  {label: "An ad", value: "ads"},
  {label: "An article or blog", value: "article"},
]

const howDidYouHearOption = (value) => HowDidYouHearAboutUs.concat(RETIRED_HOW_DID_YOU_HEAR).find(o => o.value === value);

// True when the answer can be saved: an option, plus the text when that option requires it.
const isHowDidYouHearAnswered = (selectedOption, inputText) => {
  const option = howDidYouHearOption(selectedOption);
  return Boolean(option) && (!option.inputRequired || Boolean((inputText || "").trim()));
}

const updateHowDidYouHearData = async(id, howDidYouHear, email, name, orderNumber = "", userID = "", quoteNumber = "") => {
  try{
    if(howDidYouHear && howDidYouHear.selectedOption && id){
        // This browser knows the answer right away (the checkout reads it to
        // not ask again), before the write is done.
        localStorage.setItem("howDidYouHear", JSON.stringify(howDidYouHear))
        await firestore.collection("howDidTheyHear").doc(id).set({
          howDidYouHear: howDidYouHear,
          email: email,
          name: name,
          orderNumber: orderNumber,
          userID: userID,
          quoteNumber: quoteNumber,
          timestamp: new Date().getTime(),
        }).catch((error) => {
          console.log("error in updateHowDidYouHearData", error)
        });
    }else{
      console.log("howDidYouHear or id is not valid", howDidYouHear, id);
    }
  }catch(error){
    console.log("error in updateHowDidYouHearData", error)
  }
}


const answered = (a) => Boolean(a && a.selectedOption);

const cleanAnswer = (a) => ({
  selectedOption: String(a.selectedOption || ""),
  hasInput: Boolean(a.hasInput),
  inputText: String(a.inputText || ""),
});

// The answer this browser already gave (saved by updateHowDidYouHearData), or null.
const browserHowDidYouHear = () => {
  try {
    const a = JSON.parse(localStorage.getItem("howDidYouHear"));
    return answered(a) ? cleanAnswer(a) : null;
  } catch (e) {
    return null;
  }
};

// What a signed-in person answered when creating their account (the doc keyed
// by their uid), or null. Only they and office can read it.
const accountHowDidYouHear = async (uid) => {
  if (!uid) return null;
  try {
    const doc = await firestore.collection("howDidTheyHear").doc(uid).get();
    const a = doc.exists ? doc.data().howDidYouHear : null;
    return answered(a) ? cleanAnswer(a) : null;
  } catch (e) {
    return null;
  }
};

// The latest answer on one of the person's own orders or quotes, or null.
const pastHowDidYouHear = async (uid) => {
  if (!uid) return null;
  for (const collection of ["orders", "quotes"]) {
    try {
      const snap = await firestore.collection(collection).where("userID", "==", uid).limit(50).get();
      const hit = snap.docs.map((d) => d.data()).filter((d) => answered(d.howDidYouHear))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
      if (hit) return cleanAnswer(hit.howDidYouHear);
    } catch (e) {
      console.log("howDidYouHear on past " + collection, e);
    }
  }
  return null;
};

// What a signed-in person already told us: with the account, on this browser,
// or on an earlier order or quote. null when they never did (accounts made by
// office, teams from before the question): the checkout asks them once.
const knownHowDidYouHear = async (uid) =>
  (await accountHowDidYouHear(uid)) || browserHowDidYouHear() || (await pastHowDidYouHear(uid));

// The answer an order or quote is saved with. The checkout only asks guests who
// haven't answered on this browser, and signed-in people only once, so when the
// form had no answer it takes the one they gave before; before, those orders
// came in empty and office couldn't see it (#43952). It also carries where this
// browser came from (utility/visitOrigin.js), as `origin`, when we know: the
// answer is a map the rules don't look inside, so no rule had to change.
const howDidYouHearForItem = async (formAnswer, uid) => {
  const answer = answered(formAnswer) ? cleanAnswer(formAnswer)
    : (await knownHowDidYouHear(uid)) || { selectedOption: "", hasInput: false, inputText: "" };
  const origin = storedVisitOrigin();
  return origin ? { ...answer, origin } : answer;
};

// Office: how the person heard about us, for an order or quote: its own answer,
// else what they answered with their account, else the latest answer given
// with the same email. { answer, from: "item" | "account" | "email", origin } or
// null. `origin` is where the browser that made the item came from (only on the
// item itself); with it and no answer, { answer: null, from: "none", origin }.
const findHowTheyHeard = async (item) => {
  if (!item) return null;
  const origin = (item.howDidYouHear && item.howDidYouHear.origin) || null;
  const found = await findAnswer(item);
  if (found) return { ...found, origin };
  return origin ? { answer: null, from: "none", origin } : null;
};

const findAnswer = async (item) => {
  if (answered(item.howDidYouHear)) return { answer: item.howDidYouHear, from: "item" };
  if (item.userID) {
    try {
      const doc = await firestore.collection("howDidTheyHear").doc(item.userID).get();
      if (doc.exists && answered(doc.data().howDidYouHear)) return { answer: doc.data().howDidYouHear, from: "account" };
    } catch (e) {
      console.log("howDidTheyHear by account", e);
    }
  }
  if (item.email) {
    try {
      const snap = await firestore.collection("howDidTheyHear").where("email", "==", item.email).get();
      const given = snap.docs.map((d) => d.data()).filter((d) => answered(d.howDidYouHear))
        .sort((a, b) => (b.userID ? 1 : 0) - (a.userID ? 1 : 0) || (b.timestamp || 0) - (a.timestamp || 0));
      if (given.length) return { answer: given[0].howDidYouHear, from: given[0].userID ? "account" : "email" };
    } catch (e) {
      console.log("howDidTheyHear by email", e);
    }
  }
  return null;
};

// "Google search", "Referred by a friend or colleague: Lucia".
const howDidYouHearLabel = (answer) => {
  if (!answered(answer)) return "";
  const option = howDidYouHearOption(answer.selectedOption);
  const label = option ? option.label : answer.selectedOption;
  return answer.hasInput && answer.inputText ? `${label}: ${answer.inputText}` : label;
};

export {
  updateHowDidYouHearData, HowDidYouHearAboutUs, howDidYouHearOption, isHowDidYouHearAnswered,
  browserHowDidYouHear, accountHowDidYouHear, knownHowDidYouHear, howDidYouHearForItem, findHowTheyHeard, howDidYouHearLabel,
};